import { useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  useForm,
  Controller,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GeoEventReportsMap } from 'components/GeoEventReportsMap';
import { MapType } from 'features/mapping/mapTypePolicy';
import { ReportAsset } from 'shared/types';
import { ReportMedia, Report } from 'features/reports/report.types';
import { ReportableGeoEvent } from 'features/reports/components/ReportForm/types';
import { isHazardGeoEvent } from 'features/geoEvents/geoEvent.utils';
import { API } from 'api';
import { Capacitor } from '@capacitor/core';
import { parseCoordinateFieldValue } from 'shared/utils';
import { isSupportedEmbedUrl } from 'shared/embedHelpers';
import { enqueueSnackbar } from 'notistack';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { getReportMessageValidationSchema } from 'features/wildfires/utils/getReportMessageValidationSchema';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { ReportMessageField } from 'features/reports/components/ReportMessageField';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { useInitialMapZoom } from 'hooks/useInitialMapZoom';
import { getCoordinateValidationSchema } from 'features/reports/components/ReportForm/utils';
import { UseReport } from 'features/reports/hooks/useReport/useReport.types';
import { HttpStatusCode } from 'axios';
import { MediaForm } from '../../../../components/MediaForm';

type EditReportFormProps = {
  geoEvent: ReportableGeoEvent;
  report: Report;
  onUpdateReport: UseReport['onUpdateReport'];
  isReportUpdating: boolean;
};

type FormValues = {
  geoEventId: number;
  message: string;
  asset: ReportAsset;
  mediaUrl: string;
  lat: string;
  lng: string;
  az: number;
  embedUrl: string;
};

export type ReportUpdateData = {
  message: string;
  embedUrl?: string;
  media?: Omit<ReportMedia, 'dateCreated' | 'dateModified'>[];
};

// todo: react-query function type returns can be addressed after react-query 5 upgrade
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const deleteMedia = (mediaId: number) => API.delete(`media/${mediaId}`);

const getAssetInitialValue = (
  media?: ReportMedia,
  embedUrl?: string,
): ReportAsset => {
  if (media) {
    if (media.attribution) {
      return 'alertCamera';
    }
    return 'image';
  }
  if (embedUrl) {
    return 'socialEmbed';
  }
  return null;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    width: '100%',
    height: '100%',
    display: 'flex',
  },
  formContainer: {
    overflowY: 'auto',
    height: '100%',
    width: '100%',
    maxWidth: 820,
  },
  form: {
    paddingTop: theme.spacing(2),
    paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
  },
  itemLabel: {
    textTransform: 'capitalize',
  },
}));

export const EditReportForm = (props: EditReportFormProps): JSX.Element => {
  const { geoEvent, report, onUpdateReport, isReportUpdating } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const initialZoom = useInitialMapZoom();
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up(768), {
    defaultMatches: !Capacitor.isNativePlatform(),
  });
  const queryClient = useQueryClient();

  const validationSchema = useMemo(
    () =>
      Yup.object().shape(
        {
          geoEventId: Yup.number().required(
            t('editReport.inputs.incident.required'),
          ),
          message: getReportMessageValidationSchema(t),
          mediaUrl: Yup.string(),
          lat: getCoordinateValidationSchema({
            t,
            coordType: 'latitude',
            whenPropKey: 'lng',
          }),
          lng: getCoordinateValidationSchema({
            t,
            coordType: 'longitude',
            whenPropKey: 'lat',
          }),
          az: Yup.number()
            .typeError(t('addIncidentReport.inputs.azimuth.typeError'))
            .min(0, t('addIncidentReport.inputs.azimuth.minError'))
            .max(360, t('addIncidentReport.inputs.azimuth.maxError')),
          embedUrl: Yup.string()
            .url(t('addIncidentReport.inputs.embedUrl.invalidError'))
            .test(
              'is-supported-embed-url',
              t('addIncidentReport.inputs.embedUrl.unsupportedError'),
              (value) => (value ? isSupportedEmbedUrl(value) : true),
            ),
        },
        [['lat', 'lng']],
      ),
    [t],
  );

  const initialValues: FormValues = useMemo(
    () => ({
      geoEventId: geoEvent.id,
      message: report.message,
      asset: getAssetInitialValue(report.media?.[0], report.embedUrl),
      mediaUrl: report.media?.[0]?.url || '',
      lat: report.media?.[0]?.lat?.toString() || '',
      lng: report.media?.[0]?.lng?.toString() || '',
      az: report.media?.[0]?.az || 0,
      embedUrl: report.embedUrl || '',
    }),
    [geoEvent.id, report.embedUrl, report.media, report.message],
  );

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const { control, handleSubmit, reset, watch, formState } = formMethods;

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  useEffect(() => {
    // Initial values changed, reset the form to reflect these changes.
    reset(initialValues);
  }, [reset, initialValues]);

  const goBack = (): void => {
    unblockedNavigate(`/i/${geoEvent.id}`);
  };

  const deleteMediaMutation = useMutation({
    mutationFn: () => deleteMedia(report.media[0].id),
  });

  const handleSubmitReport: SubmitHandler<FormValues> = async (values) => {
    if (!geoEvent.reporterManaged) {
      enqueueSnackbar(t('editReport.automatedGeoEventMessage'), {
        variant: 'error',
      });
      return;
    }

    try {
      const updateData: ReportUpdateData = { message: values.message };

      if (report.embedUrl) {
        updateData.embedUrl = values.embedUrl;
      }

      if (report.media?.[0]?.id) {
        if (values.mediaUrl) {
          updateData.media = [
            {
              ...report.media[0],
              lat: parseCoordinateFieldValue(values.lat, 'latitude'),
              lng: parseCoordinateFieldValue(values.lng, 'longitude'),
              az: values.az,
            },
          ];
        } else if (!values.mediaUrl) {
          updateData.media = [];
          await deleteMediaMutation.mutateAsync();
        }
      }

      await onUpdateReport(updateData as Partial<Report>);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['report', report.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['infiniteReports', geoEvent.id],
        }),
        queryClient.invalidateQueries({ queryKey: ['reports', geoEvent.id] }),
        queryClient.invalidateQueries({
          queryKey: ['initialGeoEventReport', geoEvent.id],
        }),
      ]);
      enqueueSnackbar(t('editReport.successMessage'), { variant: 'success' });
      goBack();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorStatus = error?.response?.status;
      if (errorStatus === HttpStatusCode.Forbidden) {
        enqueueSnackbar(
          'Error 403: You cannot submit or update reports for this incident.',
          { variant: 'error' },
        );
      } else if (error?.message) {
        enqueueSnackbar(error.message, { variant: 'error' });
      } else {
        logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
      }
    }
  };

  const mediaUrl = watch('mediaUrl');
  const isDisabled = isReportUpdating;

  return (
    <Box
      className={classes.root}
      sx={{
        flexDirection: isLargeMediaQuery ? 'row' : 'column',
        overflow: isLargeMediaQuery ? 'hidden' : 'auto',
      }}
      data-body-scroll-lock-ignore
    >
      <Box
        sx={{
          flex: 1,
          minHeight: isLargeMediaQuery ? '100%' : 314,
          display: 'flex',
        }}
      >
        <GeoEventReportsMap
          geoEvent={geoEvent}
          hiddenMapPixels={null}
          mapType={MapType.EditReportForm}
          reportId={report.id}
          zoom={initialZoom}
        />
      </Box>
      <Container
        maxWidth={false}
        className={isLargeMediaQuery ? classes.formContainer : undefined}
      >
        <FormProvider {...formMethods}>
          <FormWithoutEnter
            onSubmit={handleSubmit(handleSubmitReport)}
            noValidate
            className={classes.form}
          >
            <Stack spacing={2}>
              <Box sx={{ paddingTop: 1 }}>
                <Typography variant="h3">
                  {t(
                    isHazardGeoEvent(geoEvent)
                      ? 'editReport.sections.hazard'
                      : 'editReport.sections.incident',
                  )}
                </Typography>
              </Box>

              <Controller
                name="geoEventId"
                control={control}
                render={({ field, fieldState }): JSX.Element => {
                  const { ref, ...muiFieldProps } = field;
                  return (
                    <TextField
                      select
                      id="field-control-incident"
                      label={t(
                        isHazardGeoEvent(geoEvent)
                          ? 'editReport.inputs.hazard.label'
                          : 'editReport.inputs.incident.label',
                      )}
                      fullWidth
                      {...muiFieldProps}
                      inputRef={ref}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                      required
                      className={classes.itemLabel}
                      disabled
                    >
                      <MenuItem
                        value={geoEvent.id}
                        className={classes.itemLabel}
                      >
                        {geoEvent.name}
                      </MenuItem>
                    </TextField>
                  );
                }}
              />

              <Box sx={{ paddingTop: 1 }}>
                <Typography variant="h3">
                  {t('editReport.sections.update')}
                </Typography>
              </Box>

              <ReportMessageField
                name="message"
                initialValue={initialValues.message}
                currentValues={{
                  notificationType: geoEvent.notificationType,
                  isFps:
                    geoEvent.geoEventType === 'wildfire'
                      ? geoEvent.data.isFps
                      : false,
                }}
                warningRulesToSkip={['acreage', 'containment']}
              />

              {mediaUrl || report.embedUrl ? (
                <MediaForm
                  geoEvent={geoEvent}
                  report={report}
                  latFieldName="lat"
                  lngFieldName="lng"
                  azFieldName="az"
                />
              ) : null}

              <Box sx={{ paddingTop: 3 }}>
                <Typography variant="h3">
                  {t('editReport.sections.preview')}
                </Typography>
              </Box>

              <Typography align="center">
                {t('editReport.silentNotification')}
              </Typography>

              <Button
                type="submit"
                fullWidth
                size="large"
                disabled={isDisabled}
              >
                {isDisabled ? (
                  <CircularProgress size={24} />
                ) : (
                  t('editReport.buttons.save')
                )}
              </Button>
            </Stack>
          </FormWithoutEnter>
        </FormProvider>
      </Container>
    </Box>
  );
};
